import React from "react";
import "./legal.css";
import { AlertTriangle } from "lucide-react";

/**
 * Standalone, unauthenticated page at /terms.
 *
 * This is a DRAFT. MedSpaBookings OS previously had no Terms of Service
 * anywhere in the product even though it takes payment via Stripe and
 * handles patient personal data on behalf of med-spa clients. This page
 * closes that gap with reasonable, comprehensive placeholder terms - it
 * is not a substitute for review by a licensed attorney before this is
 * relied on in production. See the banner below and the PR description
 * this shipped with.
 */
const Placeholder = ({ children }) => <span className="legal-placeholder">{children}</span>;

export default function TermsOfService() {
  return (
    <div className="legal-page" data-testid="terms-of-service-page">
      <div className="legal-shell">
        <div className="legal-topbar">
          <a href="/" className="legal-brand">MedSpaBookings OS</a>
          <nav>
            <a href="/terms">Terms of Service</a>
            <a href="/privacy">Privacy Policy</a>
          </nav>
        </div>

        <div className="legal-draft-banner" data-testid="terms-draft-banner">
          <AlertTriangle size={20} />
          <div>
            <strong>DRAFT — pending legal review</strong>
            <p>
              This Terms of Service is a placeholder drafted to cover the standard sections a SaaS
              agreement needs. It has not been reviewed by a licensed attorney and must not be treated
              as a binding legal document until it is. Bracketed text such as <Placeholder>[COMPANY LEGAL NAME]</Placeholder>{" "}
              marks values that still need to be filled in before this is used in production.
            </p>
          </div>
        </div>

        <article className="legal-doc glass-panel" data-testid="terms-of-service-content">
          <h1>Terms of Service</h1>
          <p className="legal-meta">Draft version 0.1 &middot; Last updated: <Placeholder>[DATE]</Placeholder></p>

          <h2>1. Acceptance of Terms</h2>
          <p>
            These Terms of Service ("Terms") govern access to and use of MedSpaBookings OS (the "Service"),
            operated by <Placeholder>[COMPANY LEGAL NAME]</Placeholder> ("we", "us", "our"). By creating an account,
            activating a client account, or otherwise using the Service, you ("Customer", "you") agree to be
            bound by these Terms. If you do not agree, do not use the Service.
          </p>

          <h2>2. Description of Service</h2>
          <p>
            MedSpaBookings OS is a software-as-a-service platform for med-spa and aesthetics businesses that
            provides, among other things: lead and client relationship management, appointment scheduling and
            a patient-facing self-serve booking widget, patient and provider records, service/treatment menus,
            AI-assisted business audits and proposal generation, billing and subscription management via Stripe,
            and related communications (email via SendGrid, SMS/voice via Twilio). We may add, change, or remove
            features at any time.
          </p>

          <h2>3. Accounts and Account Responsibilities</h2>
          <p>
            You are responsible for maintaining the confidentiality of your login credentials and for all
            activity that occurs under your account, including accounts of your staff to whom you grant access.
            You must notify us promptly of any unauthorized use. We may suspend or terminate accounts that
            violate these Terms, present a security risk, or are associated with fraudulent or abusive activity.
          </p>
          <p>
            Accounts are activated through a time-limited activation link sent to the email address you provide.
            Activating an account confirms you accept these Terms and our Privacy Policy on behalf of the
            business you represent.
          </p>

          <h2>4. Payment, Billing, and Subscriptions</h2>
          <p>
            Paid subscriptions are billed in advance on a recurring basis through our payment processor, Stripe.
            By providing payment details you authorize us (via Stripe) to charge the applicable fees on a
            recurring basis until the subscription is cancelled. Fees are described at checkout or in your order
            form and are subject to change with reasonable prior notice.
          </p>
          <p>
            Except where required by law or expressly stated otherwise, fees are non-refundable. Failure to pay
            may result in suspension or termination of access to the Service. You are responsible for any taxes
            associated with your purchase, excluding taxes based on our net income.
          </p>

          <h2>5. Data Collection and Use</h2>
          <p>
            Our collection and use of personal data — including patient personal information you or your
            patients submit through the Service (names, contact details, dates of birth, appointment and
            treatment notes) — is described in our <a href="/privacy">Privacy Policy</a>, which is incorporated
            into these Terms by reference.
          </p>
          <p>
            As between you and us, you (the med-spa business) are the data controller for patient data you input
            into the Service, and we act as a data processor / service provider acting on your instructions,
            except where we act as controller for our own account, billing, and product-analytics data. You are
            responsible for having a lawful basis to collect and share patient data with us and for your own
            compliance obligations (including, where applicable, HIPAA or equivalent healthcare-privacy
            requirements in your jurisdiction).
          </p>

          <h2>6. Data Retention</h2>
          <p>
            We retain account and patient data for as long as your account is active, and for a reasonable
            period afterward as needed to comply with legal obligations, resolve disputes, and enforce our
            agreements, after which it is deleted or anonymized in accordance with our data retention schedule
            (to be finalized — see <Placeholder>[DATA RETENTION SCHEDULE]</Placeholder>). You may request earlier
            deletion of your account data as described in the Privacy Policy, subject to records we are required
            to keep by law.
          </p>

          <h2>7. Third-Party Service Providers</h2>
          <p>
            We rely on the following categories of third-party processors to operate the Service. This list is
            illustrative of current integrations and may change:
          </p>
          <ul>
            <li><strong>Stripe</strong> — payment processing and subscription billing.</li>
            <li><strong>SendGrid</strong> — transactional and account email delivery.</li>
            <li><strong>Twilio</strong> — SMS and voice communications.</li>
            <li><strong>MongoDB Atlas</strong> — hosted database infrastructure for storing application and patient data.</li>
          </ul>
          <p>
            Each provider processes data only as necessary to provide their respective service to us and is
            bound by its own terms and, where applicable, a data processing agreement with us.
          </p>

          <h2>8. Your Rights</h2>
          <p>
            Depending on your jurisdiction, you and your patients may have rights to access, correct, export, or
            delete personal data, and to object to or restrict certain processing. See the Privacy Policy for
            how to exercise these rights. Nothing in these Terms limits any non-waivable statutory right you
            have under applicable law.
          </p>

          <h2>9. Acceptable Use</h2>
          <p>
            You agree not to use the Service to violate any law, infringe any third party's rights, transmit
            malicious code, attempt to gain unauthorized access to the Service or other accounts, or interfere
            with the Service's normal operation.
          </p>

          <h2>10. Intellectual Property</h2>
          <p>
            We retain all right, title, and interest in the Service, including its software, design, and
            trademarks. You retain ownership of the data you submit ("Customer Data"). You grant us a limited
            license to host, process, and display Customer Data solely to provide the Service to you.
          </p>

          <h2>11. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, the Service is provided "as is" and "as available" without
            warranties of any kind, express or implied. Neither party will be liable for indirect, incidental,
            special, consequential, or punitive damages arising out of or related to these Terms or the Service.
            Our total aggregate liability for any claim arising out of or relating to these Terms or the Service
            will not exceed the amount you paid us in the <Placeholder>[LIABILITY LOOKBACK PERIOD, e.g. 12 months]</Placeholder>{" "}
            preceding the event giving rise to the claim. Nothing in this section limits liability that cannot
            be limited by law (for example, liability for gross negligence, willful misconduct, or death or
            personal injury where applicable).
          </p>

          <h2>12. Termination</h2>
          <p>
            Either party may terminate for convenience by ceasing use of the Service and cancelling any active
            subscription, subject to the notice period in your order form, if any. We may suspend or terminate
            your access immediately for a material breach of these Terms, including non-payment or a security
            or legal risk to the Service or other customers. Upon termination, your right to use the Service
            ends; data export and deletion are handled per the Privacy Policy and any applicable data retention
            schedule.
          </p>

          <h2>13. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. Material changes will be notified by posting an updated
            version on this page with a new "Last updated" date, and, where required by law or where the change
            is significant, by additional notice (e.g. email). Continued use of the Service after changes take
            effect constitutes acceptance of the revised Terms.
          </p>

          <h2>14. Governing Law</h2>
          <p>
            These Terms are governed by the laws of <Placeholder>[JURISDICTION]</Placeholder>, without regard to
            its conflict-of-laws principles, and disputes will be subject to the exclusive jurisdiction of the
            courts located in <Placeholder>[JURISDICTION]</Placeholder>, unless applicable law requires otherwise.
          </p>

          <h2>15. Contact</h2>
          <p>
            Questions about these Terms can be sent to{" "}
            <a href="mailto:support@medspabookings.co">support@medspabookings.co</a> or{" "}
            <Placeholder>[LEGAL/COMPLIANCE CONTACT EMAIL]</Placeholder>, or by mail to{" "}
            <Placeholder>[BUSINESS ADDRESS]</Placeholder>.
          </p>
        </article>

        <p className="legal-footer">
          <a href="/privacy">Privacy Policy</a> &middot; <a href="/">Back to MedSpaBookings OS</a>
        </p>
      </div>
    </div>
  );
}
